import {
  AHIcon,
  AHText,
  AHTextFonts,
  ColorType,
  TouchableBox,
  useAHTextTypeStyles,
  useSpaceSize,
  useTheme,
} from "arkh";
import {
  ActivityIndicator,
  ActivityIndicatorProps,
  Platform,
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
} from "react-native";
import React, { PropsWithChildren, useMemo } from "react";
import { HeaderBackButton } from "@react-navigation/elements";

import { FormattedMessage } from "react-intl";
import { useIntlTokens } from "../useIntlTokens";

type HeaderButtonLabelProps = PropsWithChildren<{
  style?: StyleProp<TextStyle>;
  tintColor?: string;
}>;

export const HeaderButtonLabel: React.FC<HeaderButtonLabelProps> = (props) => {
  const color = useTheme().primary;
  return (
    <AHText
      type="body"
      style={[{ color: props.tintColor ?? color, fontSize: 17 }, props.style]}
    >
      {props.children}
    </AHText>
  );
};

export const HeaderButtonLoading = (props: ActivityIndicatorProps) => {
  const color = useTheme().primary;
  return (
    <ActivityIndicator
      color={color}
      {...props}
      style={[styles.headerButton, props.style]}
    />
  );
};

export const HeaderButtonInfo = (props: { backgroundColor?: string }) => {
  const backgroundColor = useTheme().primary;
  return (
    <View style={{ marginTop: 1, marginLeft: 1 }}>
      <AHIcon type="info" forceSize={24} customColor={backgroundColor} />
    </View>
  );
};

const NewLabel = () => (
  <FormattedMessage defaultMessage="New" description="AHHeaderButton New" />
);

const InviteLabel = () => (
  <FormattedMessage
    defaultMessage="Invite"
    description="AHHeaderButton Invite"
  />
);

const ScheduleLabel = () => (
  <FormattedMessage
    defaultMessage="Schedule"
    description="AHHeaderButton Schedule"
  />
);

export const HeaderButton = ({
  nativeHeader = true,
  ...props
}: {
  ref?: React.RefObject<View | null>;
  emphazise?: boolean;
  nativeHeader?: boolean;
  paddingHorizontal?: number;
  onPress?: () => void;
  color?: ColorType;
  disabled?: boolean;
  customLabel?: string;
  type:
    | "WEB-BACK"
    | "BACK"
    | "CANCEL-BACK"
    | "CANCEL"
    | "NEXT"
    | "DONE"
    | "SAVE"
    | "CLOSE"
    | "INVITE"
    | "CREATE"
    | "ADD"
    | "EDIT"
    | "NEW"
    | "MORE"
    | "SCHEDULE"
    | "DELETE"
    | "INFO"
    | "PREVIEW";
}) => {
  const { type } = props;
  const intl_tokens = useIntlTokens();
  const subtitleStyle = useAHTextTypeStyles("subtitle");
  const theme = useTheme();
  const medium = useSpaceSize("medium");
  const padding = Platform.OS === "web" ? medium : 10;
  const label = useMemo(() => {
    switch (type) {
      case "MORE":
        return <AHIcon type="more" size="medium" spaceLeft="xlarge" />;
      case "WEB-BACK":
        return intl_tokens.Back;
      case "BACK":
        return intl_tokens.Back;
      case "CANCEL-BACK":
        return intl_tokens.Cancel;
      case "CANCEL":
        return intl_tokens.Cancel;
      case "DONE":
        return intl_tokens.Done;
      case "NEXT":
        return intl_tokens.Next;
      case "SAVE":
        return intl_tokens.Save;
      case "CLOSE":
        return intl_tokens.Close;
      case "INVITE":
        return <InviteLabel />;
      case "CREATE":
        return intl_tokens.Create;
      case "ADD":
        return intl_tokens.Add;
      case "NEW":
        return <NewLabel />;
      case "EDIT":
        return intl_tokens.Edit;
      case "SCHEDULE":
        return <ScheduleLabel />;
      case "DELETE":
        return intl_tokens.Delete;
      case "INFO":
        return <HeaderButtonInfo />;
      case "PREVIEW":
        return intl_tokens.Preview;
    }
  }, [
    intl_tokens.Add,
    intl_tokens.Back,
    intl_tokens.Cancel,
    intl_tokens.Close,
    intl_tokens.Create,
    intl_tokens.Delete,
    intl_tokens.Done,
    intl_tokens.Edit,
    intl_tokens.Next,
    intl_tokens.Preview,
    intl_tokens.Save,
    type,
  ]);

  const tintColor = props.color ? theme[props.color] : undefined;
  if (Platform.OS === "web" && (type === "CANCEL-BACK" || type === "BACK")) {
    return (
      <HeaderBackButton
        tintColor={tintColor}
        onPress={props.onPress}
        disabled={props.disabled}
        label={
          props.customLabel ??
          (type === "BACK" ? intl_tokens.Back : intl_tokens.Cancel)
        }
      />
    );
  }

  if (type === "WEB-BACK") {
    return (
      <HeaderBackButton
        tintColor={tintColor}
        onPress={props.onPress}
        disabled={props.disabled}
        label={props.customLabel ?? intl_tokens.Back}
        labelStyle={subtitleStyle.defaultStyle}
        backImage={() => <AHIcon type="arrow-left" spaceRight="xsmall" />}
        style={{
          paddingLeft: 0,
          marginLeft: 0,
        }}
      />
    );
  }

  if (type === "CANCEL-BACK" || type === "BACK") {
    return (
      <HeaderBackButton
        backImage={
          Platform.OS === "android"
            ? ({ tintColor }) => (
                <View style={{ paddingRight: 4 }}>
                  <AHIcon
                    type="arrow-left"
                    customColor={tintColor}
                    forceSize={22}
                  />
                </View>
              )
            : undefined
        }
        tintColor={tintColor ?? theme.primary}
        onPress={props.onPress}
        style={{
          marginRight: 15,
          marginLeft: Platform.OS === "ios" ? -15 : 0,
        }}
        disabled={props.disabled}
        label={
          props.customLabel ??
          (type === "BACK" ? intl_tokens.Back : intl_tokens.Cancel)
        }
      />
    );
  }

  return (
    <TouchableBox
      hitSlop={{ top: 20, bottom: 20, left: 20, right: 20 }}
      pressRetentionOffset={{
        top: 20,
        bottom: 20,
        left: 20,
        right: 20,
      }}
      onPress={props.onPress}
      style={{
        paddingHorizontal:
          props.paddingHorizontal !== undefined
            ? props.paddingHorizontal
            : Platform.OS !== "web" && nativeHeader
              ? 0
              : padding,
        paddingVertical: padding,
        opacity: props.disabled ? 0.3 : 1,
      }}
      disabled={props.disabled}
    >
      <View ref={props.ref}>
        <HeaderButtonLabel
          style={props.emphazise ? { fontFamily: AHTextFonts.BOLD } : undefined}
          tintColor={tintColor}
        >
          {props.customLabel ?? label}
        </HeaderButtonLabel>
      </View>
    </TouchableBox>
  );
};

const styles = StyleSheet.create({
  headerButton: {
    paddingHorizontal: 10,
  },
});
